import { faClose, faHouse, faQrcode } from "@fortawesome/free-solid-svg-icons"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import THEME from "../../../configuration/Theme"
import GetSpace from "../../../utils/space"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { SIZE } from "../../../configuration/Sizes"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { API_ROUTES } from "../../../configuration/RoutesApi"
import { useMemo } from "react"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"

interface PublicMenuQrCodePupopProps {
  onClose: () => void
}

export const PublicMenuQrCodePupop: React.FC<PublicMenuQrCodePupopProps> = ({ onClose }) => {
  const { t } = useTranslation("company")

  const publicLink = useMemo(() => {
    const slug = companyMemory.slug?.trim() ?? ""

    if (slug.length === 0) {
      return ""
    }

    return `${API_ROUTES.SITE}${slug}`
  }, [])

  const qrCodeUrl = useMemo(() => {
    if (publicLink.length === 0) {
      return ""
    }

    return `https://api.qrserver.com/v1/create-qr-code/?size=640x640&ecc=H&data=${encodeURIComponent(publicLink)}`
  }, [publicLink])

  const handleDownloadQrCode = async () => {
    if (qrCodeUrl.length === 0) {
      return
    }

    window.open(qrCodeUrl, "_blank", "noopener,noreferrer")

    try {
      const response = await fetch(qrCodeUrl)
      const blob = await response.blob()

      const url = window.URL.createObjectURL(blob)

      const link = document.createElement("a")
      link.href = url
      link.download = `${companyMemory.slug || "menu-publico"}-qrcode.png`

      document.body.appendChild(link)
      link.click()
      link.remove()

      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Erro ao baixar QR Code", error)
    }
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("label_title_public_menu_qr_code")}
              iconColor={THEME.colors.iconBGColor}
              icon={faQrcode}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey="pages.mystore.mycompany.publicmenuqrcodepupop.1"
                    variant="global"
                    title={t("helpPopup:global.menuQrCode.title")}
                    description={t("helpPopup:global.menuQrCode.description")}
                    bullets={t("helpPopup:global.menuQrCode.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.publicMenuQrCodePupop.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <div className="p-2">
              <InputField
                type="text"
                placeholder={t("label_link_menu_qr_code_place_holder")}
                onInvalidMessage={t("label_link_menu_onInvalidMessage")}
                icon={faHouse}
                disable={true}
                value={publicLink}
                onChange={() => {}}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.publicMenuQrCodePupop.input.linkMenuQrCodePlaceHolder"
              />

              <GetSpace top={10} />

              <Animate type={AnimationTypes.slideUpIn} duration="1.5s">
                <p className="text-sm text-slate-600 text-center">{t("label_qr_code_hint")}</p>
              </Animate>

              <GetSpace top={10} />

              <Animate type={AnimationTypes.fadeInTraslate} duration="2s">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 flex justify-center items-center min-h-44">
                  {qrCodeUrl.length > 0 ? (
                    <img src={qrCodeUrl} alt={t("label_qr_code_image_alt")} className="w-full max-w-72" />
                  ) : (
                    <span className="text-sm text-slate-500">{t("label_qr_code_missing_link")}</span>
                  )}
                </div>
              </Animate>

              <div className={defaultStyle.divisionBar}></div>

              <section className={defaultStyle.buttonsController}>
                <Button
                  sizeNumber={SIZE.buttonCloseIconSize}
                  label={t("buttons:button_download")}
                  typeButton="button"
                  type="primary"
                  size="medium"
                  disabled={qrCodeUrl.length === 0}
                  bgColor={THEME.colors.buttonBGSaveColor}
                  onClick={handleDownloadQrCode}
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.publicMenuQrCodePupop.button.download"
                />
              </section>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
